import React, {useEffect, useState} from "react"
import {AllowedToEvaluateUserOnly} from "../../containers/PermissionedUser/PermissionedUser"
import {useModal} from "react-modal-hook"
import {AddToast} from "react-toast-notifications"
import {RejectApplicationDocumentModal} from "./RejectApplicationDocument"
import {
	Application,
	ApplicationDocument,
	ApplicationDocumentSide,
	ApplicationDocumentStatus,
	ApplicationDocumentType,
	approveDocument,
	DocumentLink,
	getDocumentLink,
	PatchApplicationDocument,
	updateDocument,
} from "../../resources/application"
import {ApplicationDocumentModalHeader} from "./ApplicationDocumentModalHeader"
import {ImageCard} from "../ImageCard/ImageCard"
import Icon from "../Icon/Icon"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AsyncResult} from "../../types/asyncResult"
import {ErrorDocument, OkDocument} from "../../resources/common"
import EditDocumentForm, {editDocumentFormId} from "./EditDocument/EditDocumentForm"
import {DocumentEditMode} from "./EditMode/DocumentEditMode"
import {DocumentPreviewMode} from "./PreviewMode/DocumentPreviewMode"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import Loading from "../Loading/Loading"
import {useIsAllowUpdateDocument} from "../../services/permission"

interface ModalAsideProps {
	application: Application
	document: ApplicationDocument
	accessToken: string
	refresh: () => void
	close: () => void
	addToast: AddToast
}

export function ApplicationDocumentModalAside(props: ModalAsideProps) {
	const {application, document, accessToken, refresh, close, addToast} = props

	const [isEditMode, setIsEditMode] = useState(false)
	const [updateDocumentState, updateDocumentRequest] = useAsyncResultIdle(updateDocument)
	const isDocumentResubmitted = document.attributes.resubmitted ?? false
	const [loading, setLoading] = useState(false)

	const canReject =
		application.attributes.status !== "Approved" &&
		application.attributes.status !== "Denied" &&
		application.attributes.status !== "Pending" &&
		document.attributes.status !== ApplicationDocumentStatus.Invalid &&
		!isEditMode

	const canApprove =
		application.attributes.status !== "Approved" &&
		application.attributes.status !== "Denied" &&
		application.attributes.status !== "Pending" &&
		document.attributes.status !== ApplicationDocumentStatus.Approved &&
		!isEditMode

	const [showReject, hideReject] = useModal(() => (
		<RejectApplicationDocumentModal
			close={() => null}
			applicationId={application.id}
			documentId={document.id}
			onSuccess={() => {
				hideReject()
				close()
				refresh()
			}}
		/>
	))

	const additionalDocumentLinkState =
		document.attributes.documentType === ApplicationDocumentType.selfieVerification &&
		document.attributes.status !== ApplicationDocumentStatus.Required
			? useAsyncResult(() =>
					getDocumentLink(application.id, document.id, accessToken, ApplicationDocumentSide.Additional)
			  )
			: AsyncResult.pending<OkDocument<DocumentLink>, ErrorDocument>()

	const additionalDocumentLink = additionalDocumentLinkState.match(
		() => undefined,
		(image) => image,
		() => null
	)

	const imageUrl = additionalDocumentLink?.data?.attributes?.link
	const selfieImage = imageUrl
		? {
				src: imageUrl,
				alt: "Selfie",
		  }
		: null

	useEffect(() => {
		updateDocumentState.match(
			() => undefined,
			() => undefined,
			() => {
				addToast("Document updated successfully", {appearance: "success"})
				setIsEditMode(false)
				refresh()
				setLoading(false)
			},
			() => {
				addToast("Couldn't update information, please refresh and try again", {appearance: "error"})
				setLoading(false)
			}
		)
	}, [updateDocumentState])

	return (
		<aside className="modal-sidebar">
			<ApplicationDocumentModalHeader id={document.id} status={document.attributes.status} close={close} />
			{selfieImage && (
				<ImageCard
					title={"Selfie"}
					prefixIcon={<Icon size={16} icon={"face-id-user--interface-essential"} />}
					image={selfieImage}
				></ImageCard>
			)}
			{loading && <Loading />}
			{useIsAllowUpdateDocument(application.relationships.org.data.id, application.attributes.assignee) &&
			isEditMode ? (
				<DocumentEditMode
					className="edit-mode-container"
					onCancel={() => setIsEditMode(!isEditMode)}
					isDocumentResubmitted={isDocumentResubmitted}
					formId={editDocumentFormId}
				>
					<EditDocumentForm
						document={document}
						updateDocumentRequest={(documentData: PatchApplicationDocument) => {
							setLoading(true)
							updateDocumentRequest(accessToken, application.id, document.id, documentData)
						}}
					/>
				</DocumentEditMode>
			) : (
				<DocumentPreviewMode
					onEditButtonClick={() => setIsEditMode(!isEditMode)}
					application={application}
					document={document}
				/>
			)}
			{document.attributes.documentType !== ApplicationDocumentType.clientRequested && (
				<AllowedToEvaluateUserOnly
					applicationOrgId={application.relationships.org.data.id}
					assignee={application.attributes.assignee}
				>
					<div className="sidebar-buttons">
						<button className="button is-danger" disabled={!canReject} onClick={showReject}>
							Reject
						</button>
						<button
							className="button is-success"
							disabled={!canApprove}
							onClick={() =>
								approveDocument(accessToken, application.id, document.id).then(() => {
									close()
									refresh()
								})
							}
						>
							Approve
						</button>
					</div>
				</AllowedToEvaluateUserOnly>
			)}
		</aside>
	)
}
